import React, { useState, useCallback, useEffect, createContext, useContext } from 'react';
import { CheckCircle, XCircle, AlertTriangle, Info, X } from 'lucide-react';

const ToastContext = createContext(null);

const ICONS = {
    success: CheckCircle,
    error: XCircle,
    warning: AlertTriangle,
    info: Info,
};

const COLORS = {
    success: 'border-emerald-500/20 bg-emerald-500/10 text-emerald-400',
    error: 'border-red-500/20 bg-red-500/10 text-red-400',
    warning: 'border-amber-500/20 bg-amber-500/10 text-amber-400',
    info: 'border-blue-500/20 bg-blue-500/10 text-blue-400',
};

const ICON_COLORS = {
    success: 'text-emerald-400',
    error: 'text-red-400',
    warning: 'text-amber-400',
    info: 'text-blue-400',
};

let toastId = 0;

function ToastItem({ toast, onRemove }) {
    const [exiting, setExiting] = useState(false);
    const Icon = ICONS[toast.type] || Info;

    useEffect(() => {
        const timer = setTimeout(() => {
            setExiting(true);
            setTimeout(() => onRemove(toast.id), 300);
        }, toast.duration || 3500);
        return () => clearTimeout(timer);
    }, [toast, onRemove]);

    const handleClose = () => {
        setExiting(true);
        setTimeout(() => onRemove(toast.id), 300);
    };

    return (
        <div
            className={`flex items-start space-x-3 px-4 py-3.5 rounded-2xl border backdrop-blur-xl shadow-2xl shadow-black/30 max-w-sm w-full transition-all duration-300 ${COLORS[toast.type] || COLORS.info} ${exiting ? 'opacity-0 translate-x-4 scale-95' : 'opacity-100 translate-x-0 scale-100'}`}
            style={{ animation: exiting ? undefined : 'slideIn 0.3s cubic-bezier(0.16, 1, 0.3, 1)' }}
        >
            <Icon size={16} className={`shrink-0 mt-0.5 ${ICON_COLORS[toast.type] || ICON_COLORS.info}`} />
            <p className="text-sm flex-1 text-white/90 font-medium leading-snug">{toast.message}</p>
            <button onClick={handleClose} className="shrink-0 text-white/20 hover:text-white/60 transition-colors">
                <X size={14} />
            </button>
        </div>
    );
}

export function ToastProvider({ children }) {
    const [toasts, setToasts] = useState([]);

    const addToast = useCallback((message, type = 'info', duration = 3500) => {
        const id = ++toastId;
        setToasts(prev => [...prev, { id, message, type, duration }]);
    }, []);

    const removeToast = useCallback((id) => {
        setToasts(prev => prev.filter(t => t.id !== id));
    }, []);

    const toast = React.useMemo(() => ({
        success: (msg, dur) => addToast(msg, 'success', dur),
        error: (msg, dur) => addToast(msg, 'error', dur || 5000),
        warning: (msg, dur) => addToast(msg, 'warning', dur),
        info: (msg, dur) => addToast(msg, 'info', dur),
    }), [addToast]);

    return (
        <ToastContext.Provider value={toast}>
            {children}
            {/* Toast container — top right */}
            <div className="fixed top-4 right-4 z-[100] flex flex-col space-y-2.5 pointer-events-none">
                {toasts.map(t => (
                    <div key={t.id} className="pointer-events-auto">
                        <ToastItem toast={t} onRemove={removeToast} />
                    </div>
                ))}
            </div>
        </ToastContext.Provider>
    );
}

export function useToast() {
    const ctx = useContext(ToastContext);
    if (!ctx) throw new Error('useToast must be used within a ToastProvider');
    return ctx;
}
